// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { useGlobalSearchParams, usePathname, useRootNavigationState, useRouter, useSegments } from 'expo-router';

import { buildAuthUrl, exchangeCode, parseAuthCallback, REDIRECT_URI } from '@/auth/oidc';
import { getActiveClaimSessionSync, loadClaimSession } from '@/services/claimSession';
import type { UserSession } from '@/app/user-context';
import { useRoundState } from '@/stores/roundState';

type UseAuthFlowOptions = {
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
};

type AuthExchangeState = { code: string | null; inFlight: boolean };

WebBrowser.maybeCompleteAuthSession();

export const useAuthFlow = ({ user, setUser }: UseAuthFlowOptions) => {
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const segments = useSegments();
  const pathname = usePathname();
  const { inviteToken: inviteTokenParam } = useGlobalSearchParams<{ inviteToken?: string | string[] }>();
  const [authChecked, setAuthChecked] = useState(false);
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(Platform.OS === 'web');
  const [redirecting, setRedirecting] = useState(false);
  const authExchangeRef = useRef<AuthExchangeState>({ code: null, inFlight: false });

  const handleAuthCallbackUrl = useCallback(
    async (url: string) => {
      const { code, state } = parseAuthCallback(url);
      if (!code) return;
      if (authExchangeRef.current.inFlight || authExchangeRef.current.code === code) {
        return;
      }
      authExchangeRef.current = { code, inFlight: true };
      try {
        const auth = await exchangeCode(code, state ?? undefined);
        useRoundState.getState().clear();
        setUser({
          id: auth.id,
          forename: auth.name,
          token: auth.accessToken,
          idToken: auth.idToken,
          refreshToken: auth.refreshToken,
        });
        if (isNavigationReady) {
          router.replace('/welcome');
        }
      } catch (err) {
        console.error('Failed to complete auth callback', err);
        authExchangeRef.current = { code, inFlight: false };
      }
    },
    [isNavigationReady, router, setUser]
  );

  useEffect(() => {
    setAuthChecked(true);
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSessionLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authChecked || !claimSessionLoaded || !isNavigationReady) return;
    const hasResolvedRouteContext = !!pathname;
    if (!hasResolvedRouteContext) {
      return;
    }

    const first = segments[0];
    const second = segments[1];
    const normalizedPathname = String(pathname ?? '').toLowerCase();
    const inviteToken =
      typeof inviteTokenParam === 'string'
        ? inviteTokenParam.trim()
        : Array.isArray(inviteTokenParam)
          ? String(inviteTokenParam[0] ?? '').trim()
          : '';
    const inAuthFlow = first === 'login' || first === 'register' || first === 'callback';
    const inPublicStartRoute =
      normalizedPathname === '/' ||
      normalizedPathname === '/index';
    const inAnonymousInviteRoute =
      (first === 'tour' && second === 'invite') ||
      normalizedPathname.startsWith('/tour/invite/') ||
      (
        first === 'tour' &&
        typeof second === 'string' &&
        segments[2] === 'leaderboard' &&
        !!inviteToken
      );
    const hasGuestClaimSession = !!getActiveClaimSessionSync();
    const inRoundRoute = first === 'round' || normalizedPathname.startsWith('/round/');
    const inClaimTourLeaderboardRoute =
      hasGuestClaimSession &&
      first === 'tour' &&
      typeof second === 'string' &&
      segments[2] === 'leaderboard';
    const inGuestClaimAllowedRoute = hasGuestClaimSession && (inRoundRoute || inClaimTourLeaderboardRoute);
    if (!user && !inAuthFlow && !inPublicStartRoute && !inAnonymousInviteRoute && !inGuestClaimAllowedRoute && !redirecting) {
      (async () => {
        try {
          setRedirecting(true);
          const url = await buildAuthUrl();
          if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
            window.location.href = url;
            return;
          }

          try {
            const result = await WebBrowser.openAuthSessionAsync(url, REDIRECT_URI);
            const resultUrl = 'url' in result && typeof result.url === 'string' ? result.url : null;
            console.log('layout auth session result', result.type, resultUrl ?? '');
            if (result.type === 'success' && resultUrl) {
              const { code, state } = parseAuthCallback(resultUrl);
              if (!code) {
                setRedirecting(false);
                return;
              }
              const auth = await exchangeCode(code, state ?? undefined);
              useRoundState.getState().clear();
              setUser({
                id: auth.id,
                forename: auth.name,
                token: auth.accessToken,
                idToken: auth.idToken,
                refreshToken: auth.refreshToken,
              });
              router.replace('/welcome');
              return;
            }
          } catch (authErr) {
            console.error('Auth session failed, opening browser', authErr);
            await Linking.openURL(url);
          }

          setRedirecting(false);
        } catch (err) {
          console.error('Failed to start auth flow', err);
          setRedirecting(false);
        }
      })();
    } else if (user && inAuthFlow) {
      router.replace('/welcome');
    }
  }, [authChecked, claimSessionLoaded, isNavigationReady, pathname, user, segments, router, redirecting, setUser, inviteTokenParam]);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    const handler = ({ url }: { url: string }) => {
      console.log('deep link received', url);
      handleAuthCallbackUrl(url);
    };
    const subscription = Linking.addEventListener('url', handler);
    Linking.getInitialURL()
      .then((url) => {
        if (url) {
          console.log('deep link initial', url);
          handleAuthCallbackUrl(url);
        }
      })
      .catch(() => null);
    return () => subscription.remove();
  }, [handleAuthCallbackUrl]);

  return { authChecked };
};
